import React, { useEffect, useState, useContext } from "react";
import { getRawMaterials, getUnits, get_rawtransaction, get_raw_material_stock_report } from "../../../../Utility/Api";
import { NaviGate, shop_id } from "../../../../Utility/function";
import { WarehouseContext } from "../../../../Utility/UserContext";
import { Calendar } from "lucide-react";
import moment from "moment";
import "./../table.css";
import * as XLSX from "xlsx";

const FormattedDateInput = ({ id, label, value, onChange, min, max }) => {
    return (
        <div className="form-floating position-relative">
            <input
                type="date"
                id={id}
                value={value || ''}
                min={min}
                max={max}
                onChange={onChange}
                style={{
                    opacity: 0,
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: 10
                }}
            />
            <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{
                    paddingTop: '1.625rem',
                    paddingBottom: '0.625rem',
                    height: '58px',
                    pointerEvents: 'none',
                    backgroundColor: '#fff'
                }}
            >
                <span className={value ? "text-dark fw-medium" : "text-muted"}>
                    {value ? moment(value).format('DD/MM/YYYY') : 'DD/MM/YYYY'}
                </span>
                <Calendar size={16} className="text-secondary ms-2 shrink-0" />
            </div>
            <label htmlFor={id} style={{ zIndex: 5 }}>{label}</label>
        </div>
    );
};

const RawMaterialReport = () => {
    const { showLoader, hideLoader, shopItem } = useContext(WarehouseContext);
    
    // Tab State: 'date_summary' or 'current_stock'
    const [activeTab, setActiveTab] = useState("date_summary");

    // Raw Materials Current Stock state
    const [rawMaterials, setRawMaterials] = useState([]);
    const [unitsList, setUnitsList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [stockStatus, setStockStatus] = useState("");

    // Date-Wise Closing Stock Summary state
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [stockSummary, setStockSummary] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summarySearch, setSummarySearch] = useState("");
    const [selectedType, setSelectedType] = useState(""); // "" for all, "0" for Spices, "1" for Packaging, "2" for Liquid
    const [summaryMeta, setSummaryMeta] = useState({
        total_items: 0,
        total_closing_cost: 0,
        spices_count: 0,
        spices_total_stock: 0,
        spices_total_cost: 0,
        packaging_count: 0,
        packaging_total_stock: 0,
        packaging_total_cost: 0,
        liquid_count: 0,
        liquid_total_stock: 0,
        liquid_total_cost: 0
    });

    const [companyInfo, setCompanyInfo] = useState({
        name: "ZTR Masala",
        address: "Business Address",
        phone: "",
        email: ""
    });

    useEffect(() => {
        if (shopItem) {
            setCompanyInfo({
                name: shopItem?.title || "ZTR Masala",
                address: shopItem?.Address || "Business Address",
                phone: shopItem?.phone1 || "",
                email: shopItem?.email || ""
            });
        }
    }, [shopItem]);

    // Initial load for Date Wise Summary
    useEffect(() => {
        fetchSummaryData(selectedDate);
        fetchUnits();
    }, []);

    // Fetch Date Summary when active tab, selectedDate or shopItem changes
    useEffect(() => {
        if (activeTab === "date_summary") {
            fetchSummaryData(selectedDate);
        }
    }, [activeTab, selectedDate, shopItem]);

    // Fetch Current Stock when active tab is current_stock or shop changes
    useEffect(() => {
        if (activeTab === "current_stock") {
            fetchCurrentStockData();
        }
    }, [activeTab, shopItem]);

    const fetchSummaryData = async (dateVal = selectedDate) => {
        try {
            showLoader();
            setSummaryLoading(true);
            const currentShopId = shopItem?.shop_id || shopItem?.id || shop_id || "0";
            const response = await get_raw_material_stock_report({
                to_date: dateVal,
                date: dateVal,
                shop_id: currentShopId
            });

            if (response.data && response.data.status === "success") {
                const list = response.data.data || [];
                setStockSummary(list);
                setSummaryMeta({
                    total_items: response.data.total_items || list.length,
                    total_closing_cost: response.data.total_closing_cost || 0,
                    spices_count: response.data.spices_count || 0,
                    spices_total_stock: response.data.spices_total_stock || 0,
                    spices_total_cost: response.data.spices_total_cost || 0,
                    packaging_count: response.data.packaging_count || 0,
                    packaging_total_stock: response.data.packaging_total_stock || 0,
                    packaging_total_cost: response.data.packaging_total_cost || 0,
                    liquid_count: response.data.liquid_count || 0,
                    liquid_total_stock: response.data.liquid_total_stock || 0,
                    liquid_total_cost: response.data.liquid_total_cost || 0
                });
            } else {
                setStockSummary([]);
                setSummaryMeta({
                    total_items: 0,
                    total_closing_cost: 0,
                    spices_count: 0,
                    spices_total_stock: 0,
                    spices_total_cost: 0,
                    packaging_count: 0,
                    packaging_total_stock: 0,
                    packaging_total_cost: 0,
                    liquid_count: 0,
                    liquid_total_stock: 0,
                    liquid_total_cost: 0
                });
            }
        } catch (error) {
            console.error("Error fetching raw material stock summary:", error);
            setStockSummary([]);
        } finally {
            setSummaryLoading(false);
            hideLoader();
        }
    };

    const fetchCurrentStockData = async () => {
        try {
            showLoader();
            const currentShopId = shopItem?.shop_id || shopItem?.id || shop_id || "0";
            const response = await getRawMaterials({ shop_id: currentShopId });
            const txnResponse = await get_rawtransaction("", 100000, "", "1");

            const totalsMap = {};
            if (txnResponse.data && txnResponse.data.data) {
                txnResponse.data.data.forEach(t => {
                    const rawId = String(t.raw_mat_id);
                    if (!totalsMap[rawId]) {
                        totalsMap[rawId] = { total_qty: 0, total_price: 0 };
                    }
                    totalsMap[rawId].total_qty += parseFloat(t.qty || 0);
                    totalsMap[rawId].total_price += parseFloat(t.price || 0);
                });
            }

            if (response.data && response.data.data) {
                const enriched = response.data.data.map(item => {
                    const totals = totalsMap[String(item.id)] || { total_qty: 0, total_price: 0 };
                    return { ...item, total_qty: totals.total_qty, total_price: totals.total_price };
                });
                setRawMaterials(enriched);
            }
        } catch (error) {
            console.error("Error fetching raw materials current stock:", error);
        } finally {
            hideLoader();
        }
    };

    const fetchUnits = async () => {
        try {
            const response = await getUnits();
            if (response.data && response.data.data) {
                setUnitsList(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
        }
    };

    const getUnitName = (unitId) => {
        if (!unitId) return "";
        const found = unitsList.find(u => String(u.id) === String(unitId));
        if (found) return found.title;
        if (String(unitId) === "1") return "kg";
        if (String(unitId) === "2") return "gram";
        return unitId;
    };

    const getTypeBadge = (type) => {
        const t = String(type || '0');
        if (t === '1') {
            return (
                <span className="badge px-2 py-1" style={{ backgroundColor: "#e0e7ff", color: "#4338ca", border: "1px solid #c7d2fe", fontSize: "11px" }}>
                    Packaging Material
                </span>
            );
        }
        if (t === '2') {
            return (
                <span className="badge px-2 py-1" style={{ backgroundColor: "#cffafe", color: "#0e7490", border: "1px solid #a5f3fc", fontSize: "11px" }}>
                    Liquid
                </span>
            );
        }
        return (
            <span className="badge px-2 py-1" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontSize: "11px" }}>
                Spices
            </span>
        );
    };

    // Filter Summary by Search and Type
    const filteredSummary = stockSummary.filter(item => {
        const matchesSearch = !summarySearch.trim() || 
            (item.product_title || "").toLowerCase().includes(summarySearch.toLowerCase().trim()) ||
            (item.product_code || "").toLowerCase().includes(summarySearch.toLowerCase().trim());
        const matchesType = !selectedType || 
            (selectedType === "0" ? (String(item.type) === "0" || !item.type) : String(item.type) === String(selectedType));
        return matchesSearch && matchesType;
    });

    const filteredSummaryStock = filteredSummary.reduce((sum, item) => sum + parseFloat(item.closing_stock || 0), 0);
    const filteredSummaryCost = filteredSummary.reduce((sum, item) => sum + parseFloat(item.closing_cost || 0), 0);

    // Filter Current Stock
    const applyCurrentFilters = () => {
        let filtered = [...rawMaterials];
        if (selectedType) {
            filtered = filtered.filter(item => {
                if (selectedType === "0") {
                    return String(item.type) === "0" || !item.type;
                }
                return String(item.type) === String(selectedType);
            });
        }
        if (stockStatus === "low") {
            filtered = filtered.filter(item => {
                const stock = parseFloat(item.remaining_stock || 0);
                const reorder = parseFloat(item.reorder_point || 0);
                return stock <= reorder;
            });
        } else if (stockStatus === "instock") {
            filtered = filtered.filter(item => {
                const stock = parseFloat(item.remaining_stock || 0);
                const reorder = parseFloat(item.reorder_point || 0);
                return stock > reorder;
            });
        }
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(item =>
                (item.title && item.title.toLowerCase().includes(search)) ||
                (item.product_code && item.product_code.toLowerCase().includes(search))
            );
        }
        return filtered;
    };

    const filteredCurrentData = applyCurrentFilters();
    const lowStockCount = filteredCurrentData.filter(item =>
        parseFloat(item.remaining_stock || 0) <= parseFloat(item.reorder_point || 0)
    ).length;

    const handlePrint = () => {
        const printWindow = window.open("", "_blank");
        const printContent = document.getElementById("printable-raw-material-report").innerHTML;
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Raw Material Stock Report</title>
                <style>
                    @media print {
                        @page { size: A4 portrait; margin: 0.5in; }
                        body { font-family: Arial, sans-serif; font-size: 11pt; color: #000; background: #fff; }
                        .print-container { width: 100%; max-width: 21cm; margin: 0 auto; }
                        .print-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 15px; }
                        .print-header h1 { font-size: 18pt; margin: 0; }
                        .print-header p { margin: 4px 0; font-size: 10pt; }
                        .report-info { margin: 15px 0; padding: 10px; background-color: #f8f9fa; border-radius: 5px; display: flex; justify-content: space-between; }
                        .report-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10pt; }
                        .report-table th, .report-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        .report-table th { background-color: #f2f2f2; font-weight: bold; }
                        .text-right { text-align: right; }
                        .total-row td { font-weight: bold; background: #f8f9fa; }
                        .footer { margin-top: 30px; text-align: center; font-size: 9pt; color: #6c757d; }
                    }
                </style>
            </head>
            <body>
                ${printContent}
                <script>
                    window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 100); };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const exportSummaryToExcel = () => {
        const exportData = filteredSummary.map((item, index) => ({
            "S.No": index + 1,
            "Product Name": item.product_title,
            "Type": String(item.type) === "1" ? "Packaging Material" : String(item.type) === "2" ? "Liquid" : "Spices",
            "Closing Stock": `${parseFloat(item.closing_stock || 0).toFixed(3)} ${String(item.type) === "2" ? "li" : "kg"}`,
            "Price/Unit (INR)": item.price_unit,
            "Closing Cost (INR)": item.closing_cost
        }));
        exportData.push({
            "S.No": "",
            "Product Name": "TOTAL",
            "Type": "",
            "Closing Stock": `${filteredSummaryStock.toFixed(3)}`,
            "Price/Unit (INR)": "",
            "Closing Cost (INR)": filteredSummaryCost.toFixed(2)
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Raw_Material_Closing_Stock");
        XLSX.writeFile(wb, `Raw_Material_Closing_Stock_${moment(selectedDate).format('YYYY-MM-DD')}.xlsx`);
    };

    return (
        <div className="page-content py-1">
            <NaviGate />
            <div className="card p-3 mx-2 overflow-y-scroll" style={{ minHeight: "90vh" }}>

                {/* Tabs */}
                <div className="d-flex justify-content-center mb-4 border-bottom pb-3">
                    <div className="btn-group" role="group">
                        <button
                            type="button"
                            className={`btn ${activeTab === "date_summary" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("date_summary")}
                        >
                            <i className="bi bi-calendar-event me-1"></i> Date Wise Summary
                        </button>
                        <button
                            type="button"
                            className={`btn ${activeTab === "current_stock" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setActiveTab("current_stock")}
                        >
                            <i className="bi bi-boxes me-1"></i> Current Stock & Alerts
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                {activeTab === "date_summary" ? (
                    <div className="d-flex mb-4 border-bottom justify-content-between align-items-center flex-wrap pb-3" style={{ width: "100%" }}>
                        <h5 className="text-start w-auto fw-bold mb-0 text-uppercase">
                            Raw Material Date-Wise Summary
                        </h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            {/* Date Selector */}
                            <div style={{ width: "170px" }}>
                                <FormattedDateInput
                                    id="rm_asOfDate"
                                    label="Select Date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </div>

                            {/* Material Type Filter */}
                            <div style={{ width: "180px" }}>
                                <div className="form-floating">
                                    <select
                                        className="form-select fw-semibold"
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        id="summaryMaterialType"
                                    >
                                        <option value="">All Materials</option>
                                        <option value="0">Spices</option>
                                        <option value="1">Packaging Material</option>
                                        <option value="2">Liquid</option>
                                    </select>
                                    <label htmlFor="summaryMaterialType">Material Type</label>
                                </div>
                            </div>

                            {/* Search Product */}
                            <div style={{ width: "220px" }}>
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        value={summarySearch}
                                        onChange={(e) => setSummarySearch(e.target.value)}
                                        placeholder="Search"
                                        id="summarySearch"
                                    />
                                    <label htmlFor="summarySearch">Search Material</label>
                                </div>
                            </div>

                            {/* Export Button */}
                            <button
                                type="button"
                                className="btn btn-success p-3"
                                onClick={exportSummaryToExcel}
                                style={{ height: "58px" }}
                                title="Export to Excel"
                            >
                                <i className="bi bi-file-earmark-excel me-1"></i> Export
                            </button>

                            {/* Print Button */}
                            <button
                                type="button"
                                className="btn btn-dark p-3"
                                onClick={handlePrint}
                                style={{ height: "58px" }}
                            >
                                <i className="bi bi-printer me-1"></i> Print
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="d-flex mb-4 border-bottom justify-content-between align-items-center flex-wrap pb-3" style={{ width: "100%" }}>
                        <h5 className="text-start w-auto fw-bold mb-0 text-uppercase">
                            Raw Material Current Stock & Low Stock Alerts
                        </h5>
                        <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            <div style={{ width: "220px" }}>
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        placeholder="Search"
                                        id="searchMaterial"
                                    />
                                    <label htmlFor="searchMaterial">Search Material/Code</label>
                                </div>
                            </div>
                            <div style={{ width: "160px" }}>
                                <div className="form-floating">
                                    <select
                                        className="form-select"
                                        value={selectedType}
                                        onChange={(e) => setSelectedType(e.target.value)}
                                        id="materialType"
                                    >
                                        <option value="">All Types</option>
                                        <option value="0">Spices</option>
                                        <option value="1">Packaging material</option>
                                        <option value="2">Liquid</option>
                                    </select>
                                    <label htmlFor="materialType">Material Type</label>
                                </div>
                            </div>
                            <div style={{ width: "160px" }}>
                                <div className="form-floating">
                                    <select
                                        className="form-select"
                                        value={stockStatus}
                                        onChange={(e) => setStockStatus(e.target.value)}
                                        id="stockStatusFilter"
                                    >
                                        <option value="">All Stock Status</option>
                                        <option value="low">Low Stock Alert</option>
                                        <option value="instock">In Stock</option>
                                    </select>
                                    <label htmlFor="stockStatusFilter">Stock Status</label>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-dark p-3"
                                onClick={handlePrint}
                                style={{ height: "58px" }}
                            >
                                <i className="bi bi-printer me-1"></i> Print
                            </button>
                        </div>
                    </div>
                )}

                {/* KPI Summary Cards for Date Wise Summary (Highlighting Both Packaging & Spices) */}
                {activeTab === "date_summary" && (
                    <div className="row g-3 mb-4 text-start">
                        {/* Card 1: Total Materials In Stock */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <div className="card border-0 shadow-sm p-3 rounded-3 h-100" style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)", borderLeft: "5px solid #0284c7" }}>
                                <div className="text-muted small fw-bold text-uppercase">Materials In Stock</div>
                                <div className="fs-3 fw-bold text-primary mt-1">{filteredSummary.length}</div>
                                <div className="small text-muted mt-1">Available on {moment(selectedDate).format('DD/MM/YYYY')}</div>
                            </div>
                        </div>

                        {/* Card 2: Spices Stock & Valuation */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <div className="card border-0 shadow-sm p-3 rounded-3 h-100" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)", borderLeft: "5px solid #d97706" }}>
                                <div className="text-muted small fw-bold text-uppercase">Spices Stock</div>
                                <div className="fs-3 fw-bold text-dark mt-1">{parseFloat(summaryMeta.spices_total_stock || 0).toFixed(3)} kg</div>
                                <div className="small text-muted mt-1">
                                    Cost: <strong className="text-dark">₹{parseFloat(summaryMeta.spices_total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> ({summaryMeta.spices_count || 0} items)
                                </div>
                            </div>
                        </div>

                        {/* Card 3: Packaging Material Stock & Valuation */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <div className="card border-0 shadow-sm p-3 rounded-3 h-100" style={{ background: "linear-gradient(135deg, #e0e7ff 0%, #eef2ff 100%)", borderLeft: "5px solid #6366f1" }}>
                                <div className="text-muted small fw-bold text-uppercase">Packaging Material</div>
                                <div className="fs-3 fw-bold mt-1" style={{ color: "#4f46e5" }}>{parseFloat(summaryMeta.packaging_total_stock || 0).toFixed(3)} kg</div>
                                <div className="small text-muted mt-1">
                                    Cost: <strong className="text-dark">₹{parseFloat(summaryMeta.packaging_total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> ({summaryMeta.packaging_count || 0} items)
                                </div>
                            </div>
                        </div>

                        {/* Card 4: Total Stock Valuation */}
                        <div className="col-12 col-sm-6 col-xl-3">
                            <div className="card border-0 shadow-sm p-3 rounded-3 h-100" style={{ background: "linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)", borderLeft: "5px solid #16a34a" }}>
                                <div className="text-muted small fw-bold text-uppercase">Total Stock Costing</div>
                                <div className="fs-3 fw-bold text-success mt-1">
                                    ₹{parseFloat(summaryMeta.total_closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="small text-muted mt-1">Combined closing value</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-between mb-3 px-2">
                    <div>
                        <strong>Total Records:</strong> {activeTab === "date_summary" ? filteredSummary.length : filteredCurrentData.length}
                    </div>
                    {activeTab === "date_summary" && (
                        <div className="text-muted fw-semibold">
                            Showing As Of: <span className="text-primary">{moment(selectedDate).format('DD/MM/YYYY')}</span>
                        </div>
                    )}
                    {activeTab === "current_stock" && (
                        <div>
                            <strong>Low Stock Alerts:</strong>{" "}
                            <span className={lowStockCount > 0 ? "text-danger fw-bold" : "text-success"}>{lowStockCount}</span>
                        </div>
                    )}
                </div>

                {/* Table View */}
                <div className="table-responsive text-center my-2 w-100 position-relative">
                    {activeTab === "date_summary" ? (
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: "70px" }}>S.No</th>
                                    <th className="text-start px-3">Raw Material Name</th>
                                    <th style={{ width: "190px" }}>Material Type</th>
                                    <th style={{ width: "200px" }}>Available Stock</th>
                                    <th style={{ width: "190px" }}>Price / Unit (INR)</th>
                                    <th style={{ width: "220px" }}>Closing Cost (INR)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summaryLoading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <div className="mt-2 text-muted fw-semibold">Loading raw materials closing stock data...</div>
                                        </td>
                                    </tr>
                                ) : filteredSummary.length > 0 ? (
                                    filteredSummary.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td className="fw-semibold text-start px-3">
                                                {item.product_title || "-"}
                                                {item.product_code && (
                                                    <span className="text-muted small ms-2">({item.product_code})</span>
                                                )}
                                            </td>
                                            <td>{getTypeBadge(item.type)}</td>
                                            <td className="text-primary fw-bold">
                                                {parseFloat(item.closing_stock || 0).toFixed(3)} {String(item.type) === "2" ? "li" : "kg"}
                                            </td>
                                            <td className="text-muted fw-medium">
                                                ₹{parseFloat(item.price_unit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="fw-bold text-dark">
                                                ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4 text-muted">
                                            No raw material closing stock records found for {moment(selectedDate).format('DD/MM/YYYY')}.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {!summaryLoading && filteredSummary.length > 0 && (
                                <tfoot className="table-secondary fw-bold">
                                    <tr>
                                        <td colSpan="3" className="text-end px-3 fs-6">Total:</td>
                                        <td className="text-primary fs-6">
                                            {filteredSummaryStock.toFixed(3)} kg
                                        </td>
                                        <td></td>
                                        <td className="fs-6 text-dark">
                                            ₹{filteredSummaryCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    ) : (
                        <table className="table table-sm table-bordered table-hover align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th>S.No</th>
                                    <th>Title</th>
                                    <th>Type</th>
                                    <th>Product Code</th>
                                    <th>Reorder Point</th>
                                    <th>Latest Price (INR)</th>
                                    <th>Total Quantity</th>
                                    <th>Total Price (INR)</th>
                                    <th>Stocks Available</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCurrentData.length > 0 ? (
                                    filteredCurrentData.map((item, index) => {
                                        const stock = parseFloat(item.remaining_stock || 0);
                                        const totalQty = parseFloat(item.total_qty || 0);
                                        const totalPrice = parseFloat(item.total_price || 0);
                                        const reorder = parseFloat(item.reorder_point || 0);
                                        const isLowStock = stock <= reorder;

                                        return (
                                            <tr key={item.id} className={isLowStock ? "table-warning" : ""}>
                                                <td>{index + 1}</td>
                                                <td className="fw-semibold">{item.title}</td>
                                                <td>{getTypeBadge(item.type)}</td>
                                                <td>{item.product_code || "N/A"}</td>
                                                <td>{item.reorder_point || 0}</td>
                                                <td>{parseFloat(item.latest_price_unit || 0).toFixed(2)}</td>
                                                <td>{totalQty.toFixed(2)} {getUnitName(item.unit_id)}</td>
                                                <td>{totalPrice.toFixed(2)}</td>
                                                <td>
                                                    <span className={isLowStock ? "text-danger fw-bold" : ""}>
                                                        {stock.toFixed(2)} {getUnitName(item.unit_id)}
                                                        {isLowStock && (
                                                            <span className="ms-1 badge bg-danger text-white">Low Stock</span>
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-muted">
                                            No raw materials found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Printable Report (Hidden on screen) */}
                <div id="printable-raw-material-report" style={{ display: "none" }}>
                    <div className="print-container">
                        <div className="print-header">
                            <h1>{companyInfo.name}</h1>
                            <p>{companyInfo.address}</p>
                            <p>Phone: {companyInfo.phone} | Email: {companyInfo.email}</p>
                            <h2 style={{ marginTop: "20px", textTransform: "uppercase" }}>
                                {activeTab === "date_summary" 
                                    ? `Raw Material Closing Stock Report — As of ${moment(selectedDate).format('DD/MM/YYYY')}` 
                                    : "Raw Material Current Stock Report"}
                            </h2>
                        </div>

                        <div className="report-info" style={{ display: "flex", justifyContent: "space-between", margin: "15px 0", padding: "10px", background: "#f8f9fa" }}>
                            <div>
                                <strong>Generated On:</strong> {moment().format('DD/MM/YYYY')} at {moment().format('hh:mm A')}
                            </div>
                            <div>
                                <strong>Total Items:</strong> {activeTab === "date_summary" ? filteredSummary.length : filteredCurrentData.length}
                            </div>
                        </div>

                        {activeTab === "date_summary" ? (
                            <>
                                <table className="report-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>S.No</th>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>Product Name</th>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>Type</th>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>Remaining Stock</th>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>Price/Unit (₹)</th>
                                            <th style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>Closing Cost (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSummary.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{index + 1}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", fontWeight: "bold" }}>{item.product_title}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                                    {String(item.type) === "1" ? "Packaging Material" : String(item.type) === "2" ? "Liquid" : "Spices"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>
                                                    {parseFloat(item.closing_stock || 0).toFixed(3)} {String(item.type) === "2" ? "li" : "kg"}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>
                                                    ₹{parseFloat(item.price_unit || 0).toFixed(2)}
                                                </td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right", fontWeight: "bold" }}>
                                                    ₹{parseFloat(item.closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ fontWeight: "bold", background: "#f2f2f2" }}>
                                            <td colSpan="3" style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>
                                                Total:
                                            </td>
                                            <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right", color: "#004085" }}>
                                                {filteredSummaryStock.toFixed(3)} kg
                                            </td>
                                            <td style={{ border: "1px solid #ddd", padding: "8px" }}></td>
                                            <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>
                                                ₹{filteredSummaryCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>

                                <div className="report-totals" style={{ marginTop: "20px", padding: "15px", border: "1px solid #ddd", borderRadius: "5px", background: "#f9f9f9" }}>
                                    <h3 style={{ margin: "0 0 10px 0", fontSize: "12pt" }}>Category Breakdown</h3>
                                    <div style={{ display: "flex", justifyContent: "space-between", margin: "5px 0" }}>
                                        <span>Spices Stock & Cost:</span>
                                        <strong>{parseFloat(summaryMeta.spices_total_stock || 0).toFixed(3)} kg | ₹{parseFloat(summaryMeta.spices_total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", margin: "5px 0" }}>
                                        <span>Packaging Material Stock & Cost:</span>
                                        <strong>{parseFloat(summaryMeta.packaging_total_stock || 0).toFixed(3)} kg | ₹{parseFloat(summaryMeta.packaging_total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", margin: "8px 0", borderTop: "1px solid #ccc", paddingTop: "8px", fontSize: "12pt" }}>
                                        <span>Total Closing Stock Valuation:</span>
                                        <strong style={{ color: "#16a34a" }}>₹{parseFloat(summaryMeta.total_closing_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <table className="report-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        {["S.No", "Title", "Type", "Product Code", "Reorder Point", "Latest Price (INR)", "Total Qty", "Total Price (INR)", "Stock Available"].map(h => (
                                            <th key={h} style={{ border: "1px solid #ddd", padding: "8px", background: "#f2f2f2" }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCurrentData.map((item, index) => {
                                        const stock = parseFloat(item.remaining_stock || 0);
                                        const reorder = parseFloat(item.reorder_point || 0);
                                        const isLowStock = stock <= reorder;
                                        return (
                                            <tr key={item.id}>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{index + 1}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", fontWeight: "bold" }}>{item.title}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{String(item.type) === "1" ? "Packaging material" : String(item.type) === "2" ? "Liquid" : "Spices"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{item.product_code || "N/A"}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{item.reorder_point || 0}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{parseFloat(item.latest_price_unit || 0).toFixed(2)}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{parseFloat(item.total_qty || 0).toFixed(2)} {getUnitName(item.unit_id)}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{parseFloat(item.total_price || 0).toFixed(2)}</td>
                                                <td style={{ border: "1px solid #ddd", padding: "8px", color: isLowStock ? "#cc0000" : "#000", fontWeight: isLowStock ? "bold" : "normal" }}>
                                                    {stock.toFixed(2)} {getUnitName(item.unit_id)}{isLowStock && " (LOW)"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        <div className="footer" style={{ marginTop: "30px", textAlign: "center", fontSize: "10pt", color: "#6c757d" }}>
                            <p>ZTR Masala Inventory Management System</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default RawMaterialReport;
